import React, { useState, useEffect, useContext } from "react";
import { PatientContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";
import { translateDataValue, SYMPTOM_CATEGORIES } from "../../i18n/translateData";

import { useHistory } from "react-router-dom";

import { Row, Col, Form } from "react-bootstrap";
import Tab from "react-bootstrap/Tab";
import Nav from "react-bootstrap/Nav";
import ComponentWrapper from "./ComponentWrapper";
import SubmitButton from "../StyledComponents/SubmitButton";
import UITrackingList from "../StyledComponents/UITrackingList";
import UlPausedList from "../StyledComponents/UlPausedList";
import MedicalListSelector from "../../Components/MedicalListSelector";

import useListSelector from "../../lib/useListSelector";

import {
  postMoreTrackedFactors,
  getTBISymptoms,
  addSymptomDetails,
  getPausedList,
} from "../../api/TBIRequests";

// Apostrophe mismatches (e.g. "writer's" vs "writer’s") are handled inside
// translateDataValue, so the old hardcoded per-symptom translations are gone.
const HEADACHE_CLOSE_WORK = "Headaches caused by close work: computers, reading, gaming";

const AddSymptoms = ({ topSymptoms, setTopSymptoms }) => {
  const history = useHistory();
  const { t, language } = useTranslation();
  const { patient } = useContext(PatientContext);

  const [isAdding, setIsAdding] = useState(false);
  const [isAdded, setIsAdded] = useState(false);
  const [returnedData, setReturnedData] = useState([]);
  const [topSymptomList, setTopSymptomList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [headacheParts, setHeadacheParts] = useState([]);
  const [showErrorMsg, setShowErrorMsg] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [pauseList, setPauseList] = useState([]);
  const [pausedFactorObj, setPausedFactorObj] = useState([]);
  const [symptomMapping, setSymptomMapping] = useState({});
  // The symptom most recently paused — lets the "Symptoms Paused" section
  // jump to that symptom's category and highlight it.
  const [lastPaused, setLastPaused] = useState(null);
  // The symptom most recently unpaused — lets the "Currently Tracking" section
  // jump to that symptom's category and highlight it.
  const [lastUnpaused, setLastUnpaused] = useState(null);

  const {
    toggleFromSelectedList,
    switchSingleSelection,
    addOther,
    selectedData,
    initialize,
  } = useListSelector({
    setInitialData: setReturnedData,
    initialData: returnedData,
  });

  const tabs = Object.fromEntries(
    SYMPTOM_CATEGORIES.map((c) => [c, t(`onboarding.symptomSelector.category.${c}`)])
  );

  const colors = {
    motor: "#b3e5fc",
    sensory: "#ffccbc",
    emotional: "#dab7ec",
    behavioral: "#ffdadb",
    cognitive: "#ffdcaf",
    immune: "#fff3c4",
    metabolic: "#cef3c4",
  };

  const colorsSelected = {
    motor: "#b3e5fc",
    sensory: "#ffccbc",
    emotional: "#dab7ec",
    behavioral: "#ffdadb",
    cognitive: "#ffdcaf",
    immune: "#fff3c4",
    metabolic: "#cef3c4",
  };

  useEffect(() => {
    let isMounted = true; // Track whether the component is mounted

    getPausedList(patient?.patientId)
      .then(({ data }) => {
        if (isMounted) {
          const li = [];
          const pausedObj = [];
          data.forEach((i) => {
            if (!li.includes(i.factor)) {
              li.push(i.factor);
              pausedObj.push(i);
            }
          });
          setPauseList(li);
          setPausedFactorObj(pausedObj);
        }
      })
      .catch(() => history.push("/oops"));

    // Cleanup function
    return () => {
      isMounted = false; // Set isMounted to false when the component is unmounted
    };
  }, []);

  const changeErrorMsg = (errorCase) => {
    const errorMsgList = {
      // isOverLimit: "You are already tracking 10 symptoms",
      noSymptom: t("onboarding.symptomSelector.errors.noSymptom.neutral"),
      needHeadAcheLocation: t("onboarding.symptomSelector.errors.headacheLocation"),
    };
    setErrorMsg(errorMsgList[errorCase]);
    setShowErrorMsg(true);
  };

  const getUntrackingSymptomsObj = (list) => {
    const unTrackingList = [];
    list.forEach((symptom) => {
      if (
        !topSymptomList.includes(symptom.factor) &&
        symptom.category !== "SDOH"
      ) {
        unTrackingList.push(symptom);
      }
    });
    return unTrackingList;
  };

  const categories = [
    "motor",
    "sensory",
    "emotional",
    "behavioral",
    "cognitive",
    "immune",
    "metabolic"
  ];

  const createItemListFromRes = (data) => {
    const mapping = {};

    const itemLists = categories.map((category) => {
      const items = data
        .filter((item) => item.subcategory === category)
        .map((item) => {
          const originalSymptom = item.factor;

          // Translate for display; falls back to the original if no translation exists
          const translatedSymptom = translateDataValue(t, "onboarding.symptomListNew", category, originalSymptom);

          // Create mapping: translated → original (for reverse lookup when saving)
          if (!mapping[category]) mapping[category] = {};
          mapping[category][translatedSymptom] = originalSymptom;

          return translatedSymptom;
        });
      return { category, items };
    });

    setSymptomMapping(mapping);
    return itemLists;
  };

  const buildMoreSymptoms = async () => {
    try {
      getTBISymptoms().then(({ data }) => {
        const availableSymptoms = getUntrackingSymptomsObj(data);
        const itemList = createItemListFromRes(availableSymptoms);

        initialize(itemList);
        setReturnedData(itemList);
      });
      setIsAdding(true);
    } catch {
      history.push("/oops");
    }
  };

  const handleClose = () => {
    setIsAdded(true);
  };
  const handleSubmit = () => {
    const selectedSymptomArr = [];
    const sArr = Object.entries(selectedData);
    for (let i = 0; i < sArr.length; i++) {
      const subcategory = sArr[i][0];
      for (let j = 0; j < sArr[i][1].length; j++) {
        // Convert the displayed (translated) label back to the original English name
        const originalSymptom = symptomMapping[subcategory]?.[sArr[i][1][j]] || sArr[i][1][j];
        const curArr = [subcategory, originalSymptom];
        selectedSymptomArr.push(curArr);
      }
    }

    if (selectedSymptomArr.length === 0) {
      changeErrorMsg("noSymptom");
      return;
    }

    setSubmitting(true);

    try {
      selectedSymptomArr.forEach((selectedSymptom) => {
        if (
          selectedSymptom[1] === HEADACHE_CLOSE_WORK
        ) {
          if (headacheParts.length > 0) {
            addSymptomDetails({
              patientId: patient?.patientId,
              data: {
                symptom: HEADACHE_CLOSE_WORK,
                detail: headacheParts,
              },
            });
          } else {
            setSubmitting(false);
            changeErrorMsg("needHeadAcheLocation");
            return;
          }
        }
      });

      postMoreTrackedFactors({
        patientId: patient?.patientId,
        data: selectedSymptomArr,
      }).then((addedTopSymptoms) => {
        const newTopSymptoms = [...topSymptoms, ...addedTopSymptoms.data];
        setTopSymptoms(newTopSymptoms);
        setIsAdded(true);
        setSubmitting(false);
      });
    } catch (error) {
      history.push("/oops");
    }
  };

  useEffect(() => {
    if (topSymptoms) {
      let topList = topSymptoms.map((symptom) => symptom.factor);
      setTopSymptomList(topList);
    }
  }, [topSymptoms]);

  // Re-translate symptoms when language changes
  useEffect(() => {
    if (returnedData.length > 0) {
      getTBISymptoms().then(({ data }) => {
        const availableSymptoms = getUntrackingSymptomsObj(data);
        const itemList = createItemListFromRes(availableSymptoms);
        setReturnedData(itemList);
        initialize(itemList);
      }).catch(() => history.push("/oops"));
    }
  }, [language]);

  // Display selected symptoms in the correct language
  const displaySelectedSymptom = (category, item) => {
    const originalSymptom = symptomMapping[category]?.[item] || item;
    return translateDataValue(t, "onboarding.symptomListNew", category, originalSymptom);
  };

  return (
    <ComponentWrapper>
      {isAdded ? (
        <Row className="d-flex align-items-center justify-content-center">
          {/* <div className="ml-2">
            New symptom is added to your list to track.
          </div> */}
          <div className="d-flex justify-content-center">
            <h6
              className="pt-2 pb-2 pl-3 pr-3"
              style={{
                width: "100%",
                borderRadius: "20px",
                backgroundColor: "#3EB1A6",
                color: "white",
                textAlign: "center",
                overflowWrap: "break-word",
                cursor: "pointer"
              }}
              onClick={() => {
                setIsAdded(false);
                buildMoreSymptoms();
              }}
            >
              {t("dashboard.addSymptoms.addMoreSymptoms")}
            </h6>
          </div>
        </Row>
      ) : isAdding ? (
        <>
          <Row>
            <Col sm={12}>
              <UITrackingList
                symptoms={topSymptoms}
                pauseList={pauseList}
                setPauseList={setPauseList}
                pausedFactorObj={pausedFactorObj}
                setPausedFactorObj={setPausedFactorObj}
                setTopSymptoms={setTopSymptoms}
                setLastPaused={setLastPaused}
                lastUnpaused={lastUnpaused}
                title={t("dashboard.addSymptoms.currentlyTracking")}
              />
            </Col>
            <Col sm={12}>
              <UlPausedList
                symptoms={pauseList}
                pausedFactorObj={pausedFactorObj}
                setPausedFactorObj={setPausedFactorObj}
                setPauseList={setPauseList}
                setTopSymptoms={setTopSymptoms}
                topSymptomsList={topSymptoms}
                lastPaused={lastPaused}
                setLastUnpaused={setLastUnpaused}
                title={t("dashboard.addSymptoms.symptomPaused")}
              />
            </Col>
          </Row>
          <Row className="d-flex align-items-center justify-content-center mt-4 mb-4 mr-3">
            <Col md={10}>
              <div className="bubbleText">
                {t("dashboard.addSymptoms.unpausing.0")}
                <br />
                <br />
                {t("dashboard.addSymptoms.unpausing.1")}
              </div>
            </Col>
            <Col md={2}>
              <SubmitButton disabled={false} onClick={handleClose}>
                {t("dashboard.addSymptoms.close")}
              </SubmitButton>
            </Col>
          </Row>
          <Col sm={12}>
            <h6 style={{ textAlign: "center" }}>{t("dashboard.addSymptoms.symptoms")}</h6>
          </Col>
          <Tab.Container id="add-symptoms-tabs" defaultActiveKey="motor">
            <Row
              style={{
                marginTop: "0.5rem",
                fontWeight: "normal",
                fontSize: "15px",
              }}
            >
              <Col sm={3} style={{ paddingRight: "0px" }}>
                <Nav fill justify variant="tabs" className="flex-column">
                  {returnedData.map((itemList, i) => (
                    <Nav.Item key={i}>
                      <Nav.Link
                        className="navLink"
                        eventKey={itemList.category}
                        style={{
                          background: `${colors[itemList.category]}`,
                          border: "1px solid #106995",
                        }}
                      >
                        {" "}
                        {tabs[itemList.category]}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </Col>
              <Col sm={5} style={{ paddingRight: "0px" }}>
                <Tab.Content>
                  {returnedData.map((itemList, i) => (
                    <Tab.Pane eventKey={itemList.category} key={i}>
                      <MedicalListSelector
                        addSymptoms={true}
                        patient={patient}
                        category={itemList.category}
                        message={tabs[itemList.category]}
                        list={itemList.items}
                        index={i}
                        selectedList={selectedData[itemList.category]}
                        toggleFromSelectedList={toggleFromSelectedList}
                        selectOne={switchSingleSelection}
                        addOther={addOther}
                        setHeadacheParts={setHeadacheParts}
                        headacheParts={headacheParts}
                      />
                    </Tab.Pane>
                  ))}
                </Tab.Content>
              </Col>
              <Col sm={4}>
                <div
                  className="justify-content-left choseList"
                  style={{
                    height: "30rem",
                    overflow: "scroll",
                    color: "black",
                    border: "1px solid #106995",
                    textAlign: "left",
                    background: "#fff",
                    padding: "1rem",
                    borderRadius: "5px",
                    margin: ".25rem 0",
                  }}
                >
                  {Object.entries(selectedData).flatMap(
                    ([category, itemList]) => {
                      return itemList.map((item, i) => (
                        <div
                          style={{ margin: "0.5rem 0" }}
                          key={`${category}-${i}`}
                        >
                          <svg
                            width="24px"
                            height="24px"
                            viewBox="0 0 24 24"
                            version="1.1"
                            xmlns="http://www.w3.org/2000/svg"
                            xmlnsXlink="http://www.w3.org/1999/xlink"
                          >
                            <title>icon/action/check_ccircle_24px</title>
                            <defs>
                              <path
                                d="M12,2 C6.48,2 2,6.48 2,12 C2,17.52 6.48,22 12,22 C17.52,22 22,17.52 22,12 C22,6.48 17.52,2 12,2 Z M10,17 L5,12 L6.41,10.59 L10,14.17 L17.59,6.58 L19,8 L10,17 Z"
                                id="path-1"
                              ></path>
                            </defs>
                            <g
                              id="icon/action/check_ccircle_24px"
                              stroke="none"
                              strokeWidth="1"
                              fill="none"
                              fillRule="evenodd"
                            >
                              <mask id="mask-2" fill="white">
                                <use xlinkHref="#path-1"></use>
                              </mask>
                              <use
                                id="-↳Color"
                                fill={colorsSelected[category]}
                                xlinkHref="#path-1"
                              ></use>
                            </g>
                          </svg>{" "}
                          {displaySelectedSymptom(category, item)}
                        </div>
                      ));
                    }
                  )}
                </div>
              </Col>
            </Row>
          </Tab.Container>
          <Row>
            {showErrorMsg && (
              <Form.Control.Feedback
                style={{ display: "block" }}
                type="invalid"
              >
                {errorMsg}
              </Form.Control.Feedback>
            )}
          </Row>
          <SubmitButton disabled={submitting} onClick={handleSubmit}>
            {t("dashboard.addSymptoms.saveAndAdd")}
          </SubmitButton>
        </>
      ) : (
        <Row className="d-flex align-items-center justify-content-center">
          <div className="d-flex justify-content-center">
            <h6
              className="pt-2 pb-2 pl-3 pr-3"
              style={{
                width: "100%",
                borderRadius: "20px",
                backgroundColor: "#3EB1A6",
                color: "white",
                textAlign: "center",
                overflowWrap: "break-word",
                cursor: "pointer"
              }}
              onClick={buildMoreSymptoms}
            >
              {t("dashboard.addSymptoms.addSymptoms")}
            </h6>
          </div>
        </Row>
      )}
    </ComponentWrapper>
  );
};

export default AddSymptoms;